import { useEffect, useState } from 'react';

import type { FeatureFlag } from '@schema/features.js';

/* ───── Heuristic thresholds ─────────────────────────────────────────
 * Anything ≥ 1500px wide AND landscape is "probably DeX." Generous
 * floor — Samsung DeX targets 1080p / 4K monitors so it's almost
 * always well above this. The false-positive failure mode (a
 * landscape 16:10 tablet on a stand at 1600px) is acceptable for the
 * Pro-tier heuristic; customers who need precision upgrade to the
 * native-bridge path.
 */
const BASIC_MIN_WIDTH = 1500;

export type DexSource = 'native' | 'basic' | null;

export interface DexState {
  active: boolean;
  /** Which detector produced the current value, or null when neither
   *  is entitled / available. Surfaced for the diagnostic overlay. */
  source: DexSource;
}

interface BridgeShape {
  isDexMode?: () => string;
}

interface DexGlobals {
  MobiLauncherBridge?: BridgeShape;
  /** Push channel from the native bridge — APK calls this whenever
   *  Configuration.uiMode changes between DESK and non-DESK. */
  __mobilauncherDexResolve?: (active: boolean) => void;
}

function g(): DexGlobals {
  return window as unknown as DexGlobals;
}

/**
 * Detects whether the launcher is currently in a "desktop / DeX" mode.
 *
 * Two paths, gated by feature flags:
 *
 *   - `dexDetectionNative` (Enterprise) — preferred when available.
 *     Reads from the host APK's `MobiLauncherBridge.isDexMode()` for
 *     initial state and listens on `window.__mobilauncherDexResolve`
 *     for push updates the bridge fires on Configuration.uiMode
 *     changes.
 *   - `dexDetectionBasic` (Pro) — pure JS. Watches `resize` events
 *     and infers DeX when the viewport is wide-and-landscape.
 *     Imprecise but works in any host (browser, APK, MobiControl).
 *
 * When both flags are entitled, native wins (more accurate). When
 * native is entitled but the bridge isn't actually exposed (e.g.,
 * Enterprise tenant running in a plain browser tab), the hook falls
 * back to basic if that's also entitled.
 */
export function useDexDetector(features: ReadonlySet<FeatureFlag>): DexState {
  const hasBasic = features.has('dexDetectionBasic');
  const hasNative = features.has('dexDetectionNative');

  const [nativeActive, setNativeActive] = useState<boolean | null>(null);
  const [basicActive, setBasicActive] = useState<boolean>(false);

  // Native bridge (Enterprise). Subscribe once on mount; the bridge
  // pushes updates via the global resolver.
  useEffect(() => {
    if (!hasNative) {
      setNativeActive(null);
      return undefined;
    }
    if (typeof window === 'undefined') return undefined;
    const bridge = g().MobiLauncherBridge;
    if (typeof bridge?.isDexMode !== 'function') {
      // Entitled but no bridge — leave nativeActive null so the
      // basic fallback can kick in below.
      setNativeActive(null);
      return undefined;
    }

    // Seed from the current bridge state.
    setNativeActive(bridge.isDexMode() === 'true');

    // Install a push listener — the APK calls this on every uiMode
    // change. Single-slot resolver since this hook is used in one
    // place (LauncherHeader).
    g().__mobilauncherDexResolve = (active: boolean): void => {
      setNativeActive(active);
    };
    return () => {
      if (g().__mobilauncherDexResolve !== undefined) {
        delete g().__mobilauncherDexResolve;
      }
    };
  }, [hasNative]);

  // Basic heuristic (Pro). Only runs when the basic flag is on AND
  // native didn't produce a value — avoids contradicting the more
  // accurate path.
  useEffect(() => {
    if (!hasBasic) {
      setBasicActive(false);
      return undefined;
    }
    if (typeof window === 'undefined') return undefined;
    function check(): void {
      const w = window.innerWidth;
      const h = window.innerHeight;
      setBasicActive(w >= BASIC_MIN_WIDTH && w > h);
    }
    check();
    window.addEventListener('resize', check);
    return () => {
      window.removeEventListener('resize', check);
    };
  }, [hasBasic]);

  if (nativeActive !== null) return { active: nativeActive, source: 'native' };
  if (hasBasic) return { active: basicActive, source: 'basic' };
  return { active: false, source: null };
}
